import { LooseObjectType } from 'utils/globalTypes';
import DARK_WEB_WATCH_LIST_DATA, {
  DARK_WEB_ITEMS_MAP,
} from 'pages/dark-web-watchlist/data';
import type {
  WatchlistDataType,
  DataItemType,
} from 'pages/dark-web-watchlist/data';
import { getLastChars, capitalize } from 'utils';
import {
  VISA_NUMBER_TYPE_OPTIONS,
  ACCOUNT_TYPE_OPTIONS,
  CRYPTO_WALLET_TYPE_OPTIONS,
} from 'utils/constants';
import { WatchlistSliceType, FormatRecordType } from './types';

export function getItemType(
  value: string,
  type: 'visa' | 'account' | 'crypto',
) {
  const itemTypeOptionMap = {
    visa: VISA_NUMBER_TYPE_OPTIONS,
    account: ACCOUNT_TYPE_OPTIONS,
    crypto: CRYPTO_WALLET_TYPE_OPTIONS,
  };
  const options = itemTypeOptionMap[type] || [];

  const itemType = options.find((rec) => rec?.value === value)?.label || '';
  return capitalize(itemType);
}

function maskItemValue(value: string, unMaskCharLen = 4) {
  return `•••••${getLastChars(value, unMaskCharLen)}`;
}

export function getInitialState() {
  const initialState: WatchlistSliceType = {
    name: [],
    birthDate: [],
    ssn: [],
    motherName: [],
    email: [],
    phone: [],
    address: [],
    networkIP: [],
    license: [],
    insurance: [],
    passport: [],
    visa: [],
    taxPayer: [],
    npi: [],
    creditCard: [],
    accountNumber: [],
    cryptoWallet: [],
    loyalty: [],
    gamertag: [],
    facebook: [],
    instagram: [],
    paypal: [],
    telegram: [],
    twitter: [],
    venmo: [],
    youtube: [],
    otherUsernames: [],
    medicalRecordNumber: [],
    medicalBeneficiaryIdentifier: [],
    medicalCannabisID: [],
  };

  return initialState;
}

function parseDate(date: string) {
  return Date.parse(date);
}

export const formatNameRecord: FormatRecordType = (data) => {
  const name = [data.name.first, data.name.middle, data.name.last]
    .filter(Boolean)
    .join(' ');
  return {
    id: data.id,
    originalValue: name,
    value: capitalize(name),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatDateOfBirthRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.birthDate,
    value: '••/••/••••',
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatSSNRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.ssn.lastFour,
    value: maskItemValue(data.ssn.lastFour),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatMotherNameRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.mothersMaidenName.name,
    value: maskItemValue(data.mothersMaidenName.name),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatEmailRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.emailAddress,
    value: data.emailAddress ?? '',
    status: data.status,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatPhoneRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.phone.number,
    value: `${maskItemValue(data.phone.number)}${data.phone.type === 'unknown' ? '' : ` · ${capitalize(data.phone.type)}`}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatAddressRecord: FormatRecordType = (data) => {
  const address = `${data.address.street1 ?? ''},${data.address.street2 ? ` ${data.address.street2},` : ''} ${data.address.city ?? ''}, ${data.address.state ?? ''} ${data.address.zip ?? ''}`;
  return {
    id: data.id,
    originalValue: address,
    value: address,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatIPAdressRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.network_ip.lastFour,
    value: `${maskItemValue(data.network_ip.lastFour, 6)} · ${capitalize(data.network_ip.device_name)}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatHealthInsuranceRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.healthInfo.lastFour,
    value: `${maskItemValue(data.healthInfo.lastFour)} · ${capitalize(data.healthInfo.insuranceProvider)}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatDriverLicenseRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.driverLicense.lastFour,
    value: maskItemValue(data.driverLicense.lastFour),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatPassportRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.passport.lastFour,
    value: maskItemValue(data.passport.lastFour),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatGreenCardOrVisaRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.visaNumber.lastFour,
    value: `${maskItemValue(data.visaNumber.lastFour)} · ${getItemType(data.visaNumber.type, 'visa')}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatNPIRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.nationalProviderIdentifier.lastFour,
    value: maskItemValue(data.nationalProviderIdentifier.lastFour),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatTaxIdRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.taxId.lastFour,
    value: maskItemValue(data.taxId.lastFour),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatCreditCardRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.creditCard.lastFour,
    value: `${maskItemValue(data.creditCard.lastFour)} · ${capitalize(data.creditCard.type)}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatFinancialAccountRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.financialAccount.lastFour,
    value: `${maskItemValue(data.financialAccount.lastFour)} · ${getItemType(data.financialAccount.type, 'account')}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatCryptoWalletRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.cryptoWallet.walletId,
    value: `${maskItemValue(data.cryptoWallet.walletId)} · ${getItemType(data.cryptoWallet.walletType, 'crypto')}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatLoyaltyCardRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.loyaltyCard.lastFour,
    value: `${maskItemValue(data.loyaltyCard.lastFour)} · ${capitalize(data.loyaltyCard.name)}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatGamerTagRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.digid_gamertag.lastFour,
    value: maskItemValue(data.digid_gamertag.lastFour),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatFacebookRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.onlineProfileFacebook.name,
    value: maskItemValue(data.onlineProfileFacebook.name),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatInstagramRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.onlineProfileInstagram.name,
    value: maskItemValue(data.onlineProfileInstagram.name),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatPaypalRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.onlineProfilePaypal.name,
    value: maskItemValue(data.onlineProfilePaypal.name),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatTelegramRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.onlineProfileTelegram.name,
    value: maskItemValue(data.onlineProfileTelegram.name),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatTwitterRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.onlineProfileTwitter.name,
    value: maskItemValue(data.onlineProfileTwitter.name),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatVenmoRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.onlineProfileVenmo.name,
    value: maskItemValue(data.onlineProfileVenmo.name),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatYoutubeRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.onlineProfileYoutube.name,
    value: maskItemValue(data.onlineProfileYoutube.name),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatOtherUsernameRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.username.name,
    value: `${maskItemValue(data.username.name)} · ${capitalize(data.username.account)}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatMedicalRecordNumberRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.medicalRecordNumber.name,
    value: `${maskItemValue(data.medicalRecordNumber.lastFour)} · ${capitalize(data.medicalRecordNumber.name)}`,
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatMedicalBeneficiaryIdentifierRecord: FormatRecordType = (
  data,
) => {
  return {
    id: data.id,
    originalValue: data.medicalBeneficiaryIdentifier.lastFour,
    value: maskItemValue(data.medicalBeneficiaryIdentifier.lastFour),
    timeStamp: parseDate(data.updatedAt),
  };
};

export const formatMedicalCannabisIDRecord: FormatRecordType = (data) => {
  return {
    id: data.id,
    originalValue: data.medicalCannabisId.number,
    value: maskItemValue(data.medicalCannabisId.number),
    timeStamp: parseDate(data.updatedAt),
  };
};

export function formatWatchlistData(
  watchListData: LooseObjectType[],
): WatchlistSliceType {
  const initialData = getInitialState();

  return watchListData.reduce((acc: WatchlistSliceType, data) => {
    switch (data.itemType) {
      case DARK_WEB_ITEMS_MAP.name.itemType:
        acc.name.push(formatNameRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.birthDate.itemType:
        acc.birthDate.push(formatDateOfBirthRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.ssn.itemType:
        acc.ssn.push(formatSSNRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.motherName.itemType:
        acc.motherName.push(formatMotherNameRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.email.itemType:
        acc.email.push(formatEmailRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.phone.itemType:
        acc.phone.push(formatPhoneRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.address.itemType:
        acc.address.push(formatAddressRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.networkIP.itemType:
        acc.networkIP.push(formatIPAdressRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.license.itemType:
        acc.license.push(formatDriverLicenseRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.insurance.itemType:
        acc.insurance.push(formatHealthInsuranceRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.passport.itemType:
        acc.passport.push(formatPassportRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.visa.itemType:
        acc.visa.push(formatGreenCardOrVisaRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.taxPayer.itemType:
        acc.taxPayer.push(formatTaxIdRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.npi.itemType:
        acc.npi.push(formatNPIRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.creditCard.itemType:
        acc.creditCard.push(formatCreditCardRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.accountNumber.itemType:
        acc.accountNumber.push(formatFinancialAccountRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.cryptoWallet.itemType:
        acc.cryptoWallet.push(formatCryptoWalletRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.loyalty.itemType:
        acc.loyalty.push(formatLoyaltyCardRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.gamertag.itemType:
        acc.gamertag.push(formatGamerTagRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.facebook.itemType:
        acc.facebook.push(formatFacebookRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.instagram.itemType:
        acc.instagram.push(formatInstagramRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.paypal.itemType:
        acc.paypal.push(formatPaypalRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.telegram.itemType:
        acc.telegram.push(formatTelegramRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.twitter.itemType:
        acc.twitter.push(formatTwitterRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.venmo.itemType:
        acc.venmo.push(formatVenmoRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.youtube.itemType:
        acc.youtube.push(formatYoutubeRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.otherUsernames.itemType:
        acc.otherUsernames.push(formatOtherUsernameRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.medicalRecordNumber.itemType:
        acc.medicalRecordNumber.push(formatMedicalRecordNumberRecord(data));
        return acc;
      case DARK_WEB_ITEMS_MAP.medicalBeneficiaryIdentifier.itemType:
        acc.medicalBeneficiaryIdentifier.push(
          formatMedicalBeneficiaryIdentifierRecord(data),
        );
        return acc;
      case DARK_WEB_ITEMS_MAP.medicalCannabisID.itemType:
        acc.medicalCannabisID.push(formatMedicalCannabisIDRecord(data));
        return acc;
      default:
        return acc;
    }
  }, initialData);
}

export function getWatchlistTypeIcons() {
  const itemsMap = {} as Record<keyof WatchlistSliceType, string>;

  Object.keys(DARK_WEB_WATCH_LIST_DATA).forEach((watchlistKey) => {
    const key = watchlistKey as keyof WatchlistDataType;
    DARK_WEB_WATCH_LIST_DATA[key].data.forEach((item) => {
      itemsMap[item.id] = item.icon;
    });
  });

  return itemsMap;
}

/**
 * Calculates the total count of monitored items and max monitored items across all watchlist items.
 * @param watchlistItems - Array of watchlist item definitions
 * @param watchlistData - Object containing the actual watchlist data for each item type
 * @returns Object containing the total count of monitoredItems and maxMonitoredItems
 */
export function getWatchlistBadgeCount(
  watchlistItems: DataItemType[],
  watchlistData: WatchlistSliceType,
) {
  return watchlistItems.reduce(
    (acc, item) => {
      return {
        monitoredItems: acc.monitoredItems + watchlistData[item.id].length,
        maxMonitoredItems: acc.maxMonitoredItems + item.maxMonitoredItems,
      };
    },
    {
      monitoredItems: 0,
      maxMonitoredItems: 0,
    },
  );
}
